import React, { ReactElement } from "react"
import { PhoneNumberCollectionModal } from "../account/PhoneNumberCollectionModal"
import { CarrotPlansGatedAccountContainer } from "../../carrot-plans/case-rate-renewals/CarrotPlansGatedAccountModal"
import { useGetCaseRateRenewalStatus } from "../../carrot-plans/case-rate-renewals/useQueryCaseRateRenewals"
import { useCurrentUser } from "../../context/user/UserContext"
import { useUserRole } from "../../partner-access/hooks/usePartnerAccess"
import queryString from "query-string"
import {
  getHasCheckInModalTriggeredThisSession,
  getHasCrrModalTriggeredThisSession,
  getHasJourneyChangeModalTriggeredThisSession,
  updateHasCrrModalTriggeredThisSession
} from "./utils/localStorageUtils"
import { JourneyChangeDialogContainer } from "#/components/carrot-plans/journey-change/JourneyChangeDialog"
import { CarrotPlansPaths, DbgPaths, Paths, QueryStrings } from "#/utils/Paths"
import { CheckInTimeIntroContainer } from "#/components/carrot-plans/check-in/CheckInTimeIntro"
import {
  CarrotPlanRequestType,
  useGetInProgressCarrotPlanRequestType
} from "#/components/carrot-plans/hooks/useGetInProgressCarrotPlanRequestType"
import { Redirect, useHistory, useLocation } from "react-router-dom"
import { useOnLoginCarrotPlanAnniversaryCheck } from "#/components/views/modal/hooks/useOnLoginCarrotPlanAnniversaryCheck"
import { useCalibratedEngagementEnabled } from "#/hooks/calibratedEngagement/useCalibratedEngagement"
import { useGetCarrotPlanRequestStatus } from "#/components/carrot-plans/hooks/useCarrotPlanRequestStatus"
import { useIsMemberInSensitiveState } from "#/components/carrot-plans/hooks/useIsMemberInSensitiveState"
import { useSprintsAuthorizationQuery } from "#/services/user/hooks/useSprintsQuery"
import useCarrotPlanEmployeeSettings from "#/components/carrot-plans/hooks/useCarrotPlanEmployeeSettings"

export function AppLoginModalContainer(): ReactElement {
  const hasCrrModalTriggeredThisSession = getHasCrrModalTriggeredThisSession()
  const hasJourneyChangeModalTriggeredThisSession = getHasJourneyChangeModalTriggeredThisSession()
  const hasCheckInModalTriggeredThisSession = getHasCheckInModalTriggeredThisSession()
  const hasAnyRoutingFlowModalTriggeredThisSession =
    hasCrrModalTriggeredThisSession || hasJourneyChangeModalTriggeredThisSession || hasCheckInModalTriggeredThisSession

  const history = useHistory()
  const { phoneNumber, dismissedPhoneNumberFeatureModal, isInAppLockdown } = useCurrentUser()
  const { isPartnerAccessingAccount, isUserRoleLoading } = useUserRole()
  const calibratedEngagement = useCalibratedEngagementEnabled()
  const cpRequestStatusQuery = useGetCarrotPlanRequestStatus()
  const { isDoneForNow } = useCarrotPlanEmployeeSettings()
  const { data: shouldShowSprintsPage } = useSprintsAuthorizationQuery({
    select: (data) => data.memberIsSprintsAuthorized || data.partnerIsSprintsAuthorized
  })
  const { data: inProgressCarrotPlanRequestType, isLoading: isInProgressRequestTypeLoading } =
    useGetInProgressCarrotPlanRequestType()
  const {
    isLoading: isCrrLoading,
    showAdvancedCheckInFeatures,
    requiresGatedModalAttention,
    isCrrAdvancedCheckIn
  } = useGetCaseRateRenewalStatus()

  const isDataLoading =
    isCrrLoading ||
    isUserRoleLoading ||
    isInProgressRequestTypeLoading ||
    calibratedEngagement.isLoading ||
    cpRequestStatusQuery.isLoading

  const { dialog } = queryString.parse(window.location.search)
  const { pathname } = useLocation()
  const isJourneyChangeEmailQueryString = dialog === QueryStrings.JOURNEY_CHANGE_EMAIL
  const isJourneyChangeCtaQueryString = dialog === QueryStrings.JOURNEY_CHANGE_CTA
  const isCheckInEmailQueryString = dialog === QueryStrings.CHECK_IN_EMAIL
  const isCheckInFreshEmailQueryString = dialog === QueryStrings.CHECK_IN_FRESH_EMAIL
  const isCheckInCtaQueryString = dialog === QueryStrings.CHECK_IN_CTA
  const isSprintsLandingPage = pathname === Paths.SPRINTS_LANDING_PAGE
  const isMemberInSensitiveState = useIsMemberInSensitiveState()

  const isCalibratedEngagementEnabled = calibratedEngagement.data
  const hideAdvancedCheckInCRRModal =
    isCrrAdvancedCheckIn && (isCalibratedEngagementEnabled || isMemberInSensitiveState)
  const shouldCrrModalRender =
    requiresGatedModalAttention && !hideAdvancedCheckInCRRModal && !(isSprintsLandingPage && shouldShowSprintsPage)

  const shouldRenderPhoneNumberModal =
    !isPartnerAccessingAccount &&
    !shouldCrrModalRender &&
    !hasCrrModalTriggeredThisSession &&
    !hasJourneyChangeModalTriggeredThisSession &&
    !hasCheckInModalTriggeredThisSession &&
    !phoneNumber &&
    !dismissedPhoneNumberFeatureModal

  const shouldRenderCheckInModalFromEmail =
    (isCheckInEmailQueryString || isCheckInFreshEmailQueryString) &&
    !hasAnyRoutingFlowModalTriggeredThisSession &&
    !cpRequestStatusQuery.data?.carrotPlanIsOnTheWay &&
    !isDoneForNow

  const shouldRenderCheckInModalFromCta =
    isCheckInCtaQueryString && !cpRequestStatusQuery.data?.carrotPlanIsOnTheWay && !isMemberInSensitiveState

  const shouldRenderJourneyChangeModalFromEmail =
    isJourneyChangeEmailQueryString &&
    !hasAnyRoutingFlowModalTriggeredThisSession &&
    !cpRequestStatusQuery.data?.carrotPlanIsOnTheWay

  const shouldRenderJourneyChangeModalFromCta =
    isJourneyChangeCtaQueryString && !cpRequestStatusQuery.data?.carrotPlanIsOnTheWay

  const shouldRenderAnyRoutingFlowModal =
    shouldCrrModalRender ||
    shouldRenderJourneyChangeModalFromEmail ||
    shouldRenderCheckInModalFromEmail ||
    isJourneyChangeCtaQueryString ||
    isCheckInCtaQueryString ||
    isCheckInFreshEmailQueryString

  useOnLoginCarrotPlanAnniversaryCheck()

  if (!hasCrrModalTriggeredThisSession) {
    updateHasCrrModalTriggeredThisSession(shouldCrrModalRender)
  }

  if (isDataLoading) {
    return null
  }

  // CRR modal should always have 1st priority
  if (shouldCrrModalRender) {
    return (
      <CarrotPlansGatedAccountContainer
        shouldCrrModalRender={shouldCrrModalRender}
        showAdvancedCheckInFeatures={showAdvancedCheckInFeatures}
        isCrrAdvancedCheckIn={isCrrAdvancedCheckIn}
      />
    )
  }

  // If a routing modal is triggered but there's an in-progress request,
  // prioritize the modal related to their current request to continue their flow
  if (shouldRenderAnyRoutingFlowModal) {
    switch (inProgressCarrotPlanRequestType) {
      case CarrotPlanRequestType.INITIAL_INTAKE:
        return <Redirect to={CarrotPlansPaths.ENROLLMENT} />
      case CarrotPlanRequestType.RENEWAL_INTAKE:
        return (
          <CarrotPlansGatedAccountContainer
            shouldCrrModalRender={shouldCrrModalRender}
            showAdvancedCheckInFeatures={showAdvancedCheckInFeatures}
            isCrrAdvancedCheckIn={isCrrAdvancedCheckIn}
          />
        )
      case CarrotPlanRequestType.JOURNEY_CHANGE_INTAKE:
        return <JourneyChangeDialogContainer origin="email" />
      case CarrotPlanRequestType.CHECK_IN_INTAKE:
        return <CheckInTimeIntroContainer origin="email" />
    }
  }

  if (shouldRenderCheckInModalFromEmail) {
    if (isInAppLockdown) {
      return <Redirect to={DbgPaths.METABOLIC_FERTILITY_HEALTH} />
    }
    return <CheckInTimeIntroContainer origin={isCheckInFreshEmailQueryString ? "fresh" : "email"} />
  }

  if (shouldRenderJourneyChangeModalFromEmail) {
    return <JourneyChangeDialogContainer origin="email" />
  }

  if (shouldRenderPhoneNumberModal) {
    return <PhoneNumberCollectionModal />
  }

  // Handle CTA redirect modals last (these have lowest priority)
  if (shouldRenderJourneyChangeModalFromCta) {
    return <JourneyChangeDialogContainer origin="cta" setToggleModalFromCta={() => history.push("/")} />
  }

  if (shouldRenderCheckInModalFromCta) {
    return <CheckInTimeIntroContainer origin="cta" setToggleModalFromCta={() => history.push("/")} />
  }

  return null
}
